import {
    caravanSendExplanationEmail,
    deleteDossier,
    enableBovagModal,
    setFinished,
    togglePrivacy,
    updateDossier,
} from "../../../app/rootReducer";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { PatchStepsEnum } from "../../../common/constants/PatchStepsEnum";
import { setGeneralStep } from "../../helpType/helpTypeSlice";
import { GeneralStepsEnum } from "../../../common/constants/GeneralStepsEnum";
import { dossierTypeEnum } from "../../../common/constants/dossierTypeEnum";
import { useParams } from "react-router-dom";
import { fundingTypeEnum } from "../../../common/constants/fundingTypeEnum";
import { JSX, useEffect } from "react";

export const SubmitButton = (): JSX.Element | null => {
    const termsAcceptedText =
        "Je geeft hierbij opdracht aan ABS Autoherstel om de reparatie in werking te zetten. Hiermee ga je akkoord dat ABS Autoherstel jouw gegevens deelt met de vestiging van jouw keuze. De vestiging zal contact met je opnemen om de afspraak te maken. Als je niet wilt dat wij jouw gegevens doorgeven kun je via deze weg geen afspraak maken. Je kunt natuurlijk altijd zelf contact opnemen met de vestiging.";
    const privacyAcceptedText = "Ik ga akkoord met de privacyverklaring.";
    const bovagAcceptedText =
        "Ik ga akkoord met de algemene voorwaarden volgens BOVAG.";
    const dispatch = useAppDispatch();
    const root = useAppSelector((state) => state.root);
    const { privacyAccepted, bovagAccepted, dossier, registrationLink } = root;
    const { activeHelpType } = useAppSelector((state) => state.helpType);
    const isCaravanDamage = activeHelpType === dossierTypeEnum.CaravanRepair;
    const { slug } = useParams();
    const localDossierUuidKey = slug + "-dossier_uuid";
    const localDossierAccessTokenKey = slug + "-dossier_access_token";

    const agendaMoments = useAppSelector(
        (state) => state.appointment.agendaMoments,
    );

    useEffect(() => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
        });
    }, []);

    const submit = async (): Promise<void> => {
        if (isCaravanDamage && root?.dossier) {
            const dossierUuid =
                root.dossier.dossier_uuid ||
                localStorage.getItem(localDossierUuidKey) ||
                "";
            const dossierAccessToken = root.dossier.dossier_access_token || "";
            const establishmentId =
                agendaMoments.activeAgendaMoment?.establishment.id || 0;

            dispatch(
                caravanSendExplanationEmail({
                    dossierUuid,
                    dossierAccessToken,
                    explanationCaravanDamage: "",
                    establishmentId,
                }),
            ).then(() => {
                dispatch(
                    deleteDossier({
                        dossierUuid,
                        fundingType: fundingTypeEnum.Unknown,
                    }),
                );
                dispatch(setFinished(true));
                dispatch(setGeneralStep(GeneralStepsEnum.Finished));
                localStorage.removeItem(localDossierUuidKey);
                localStorage.removeItem(localDossierAccessTokenKey);
            });
        }

        if (!isCaravanDamage) {
            try {
                await dispatch(
                    updateDossier({
                        data: {
                            confirm_appointment: 1,
                            terms_accepted: 1,
                            privacy_accepted: privacyAccepted ? 1 : 0,
                            bovag_accepted: bovagAccepted ? 1 : 0,
                            terms_accepted_text: termsAcceptedText,
                            privacy_accepted_text: privacyAcceptedText,
                            bovag_accepted_text: bovagAcceptedText,
                        },
                        method: PatchStepsEnum.PatchConfirmAppointment,
                    }),
                );

                dispatch(setFinished(true));
                dispatch(setGeneralStep(GeneralStepsEnum.Finished));
            } catch (e) {
                console.error(e);
            }
        }
    };

    if (dossier) {
        return (
            <div className="s-appointment__group border-color-primary c-submit-section">
                <h1 className="s-appointment__group__title c-submit-section__title">
                    Bevestig je schademelding
                </h1>
                <div className="c-submit-section__content">
                    <div>
                        <div className="c-submit-section__intro">
                            Voordat je verdergaat, vragen we je om de
                            BOVAG-voorwaarden te bekijken.
                        </div>
                        <button
                            type="button"
                            className="c-submit-section__link"
                            onClick={() => dispatch(enableBovagModal())}
                        >
                            Bekijk en accepteer de BOVAG-voorwaarden om verder
                            te gaan
                        </button>
                    </div>

                    <div className="c-submit-section__checkboxes">
                        <div
                            className={`c-submit-section__checkbox-group ${!bovagAccepted ? "c-submit-section__checkbox-group--disabled" : ""} c-submit-section__checkbox-group--column`}
                        >
                            <div className="c-submit-section__checkbox-group">
                                <div
                                    className={`c-submit-section__custom-checkbox ${bovagAccepted ? "c-submit-section__custom-checkbox--active" : ""}`}
                                />
                                <span className="c-submit-section__checkbox-label">
                                    Ik ga akkoord met de BOVAG-voorwaarden
                                </span>
                            </div>
                            {!bovagAccepted && (
                                <div className="c-submit-section__checkbox-info">
                                    Bekijk en accepteer eerst de voorwaarden
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="c-submit-section__checkboxes">
                        <button
                            type="button"
                            className="c-submit-section__checkbox-group"
                            onClick={() => dispatch(togglePrivacy())}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    dispatch(togglePrivacy());
                                }
                            }}
                        >
                            <div
                                className={`c-submit-section__custom-checkbox ${privacyAccepted ? "c-submit-section__custom-checkbox--active" : ""}`}
                            />
                            <span className="c-submit-section__checkbox-label c-submit-section__checkbox-label--privacy">
                                Ik ga akkoord met de{" "}
                                <a
                                    href={
                                        registrationLink.privacy_agreement_url
                                    }
                                    target={"_blank"}
                                    rel="noreferrer"
                                >
                                    privacyverklaring
                                </a>{" "}
                                .
                            </span>
                        </button>
                    </div>

                    <div className="c-submit-section__legal-text">
                        Je geeft hierbij opdracht aan ABS Autoherstel om de
                        reparatie in werking te zetten. Hiermee ga je akkoord
                        dat ABS Autoherstel jouw gegevens deelt met de vestiging
                        van jouw keuze. De vestiging zal contact met je opnemen
                        om de afspraak te maken. Als je niet wilt dat wij jouw
                        gegevens doorgeven kun je via deze weg geen afspraak
                        maken. Je kunt natuurlijk altijd zelf contact opnemen
                        met de vestiging.
                    </div>

                    <div className="c-submit-section__button-wrapper">
                        <button
                            type="button"
                            disabled={
                                !root.bovagAccepted || !root.privacyAccepted
                            }
                            onClick={() => submit()}
                            className="c-submit-section__button"
                        >
                            <span className="c-submit-section__button-text">
                                Schademelding bevestigen
                            </span>
                        </button>
                        {(!root.bovagAccepted || !root.privacyAccepted) && (
                            <div
                                className={`c-submit-section__button-info ${!root.bovagAccepted || !root.privacyAccepted ? "c-submit-section__button-info--disabled" : ""}`}
                            >
                                Ga akkoord met de BOVAG-voorwaarden en
                                privacyverklaring om verder te gaan
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    } else return null;
};
